import CheckIcon from "@mui/icons-material/Check";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import {Box, IconButton, Stack, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import {ColumnDef} from "@tanstack/react-table";
import SimpleDialog from "crqlar-react-common/dist/components/SimpleDialog";
import {DataType, DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import {
    SurveyInvitation,
    SurveyInvitationChannel,
    SurveyInvitationGuestCategory,
    SurveyInvitationStatus
} from "crqlar-react-common/dist/models/survey/surveyInvitation.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {enqueueSnackbar} from "notistack";
import {useMemo, useState} from "react";
import {useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTablePrint,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    type ServerTableUrlStateOptions,
    useServerTable
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import SettingsListPage from "../../system/layout/SettingsListPage";
import {SurveyInvitationDialog} from "../share/SurveyInvitationLink";
import {SURVEY_INVITATIONS_TABLE_ID, useSurveyInvitationRefresh} from "../shared/surveyQueryRefresh";
import {filterDateRange, filterEnumValues, filterNumberValues, filterTextValues} from "../shared/surveyTableFilters";
import {useAllSurveys} from "../shared/useAllSurveys";
import SurveyInvitationStatusChip from "./SurveyInvitationStatusChip";

const ALL_INVITATION_STATUSES = Object.values(SurveyInvitationStatus);

// Links from a survey land here as `?surveyId=<id>` (see surveyListLinks).
const INVITATIONS_URL_STATE: ServerTableUrlStateOptions = {
    filterAliases: [{
        queryParam: 'surveyId',
        field: 'surveyId',
        operator: DataTypeOperator.IN
    }]
};

const SurveyInvitationsListPage = () => {
    useTrackScreenVisit('system survey invitations');

    const {t} = useLocalize();
    const navigate = useNavigate();
    const readOnly = useReadOnly();
    const refreshInvitations = useSurveyInvitationRefresh();

    const [selectedInvitation, setSelectedInvitation] = useState<SurveyInvitation | null>(null);
    const [voidTarget, setVoidTarget] = useState<SurveyInvitation | null>(null);
    const [voiding, setVoiding] = useState(false);

    const {surveys} = useAllSurveys();

    const filterOptions = useMemo<FilterOption[]>(() => [
        {
            field: 'sentAt',
            name: t('Sent'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.BETWEEN],
            predefinedValues: [],
            dataType: DataType.TIMESTAMP
        },
        {
            field: 'surveyId',
            name: t('Survey'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: surveys.map(survey => ({
                cid: survey.id,
                name: survey.name || survey.id,
                colorCode: ''
            })),
            enumValues: surveys.map(survey => survey.id),
            dataType: DataType.ENUM
        },
        {
            field: 'guestLanguage',
            name: t('Language'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: [],
            dataType: DataType.TEXT,
            idType: 'language_code_iso_639_1'
        },
        {
            field: 'status',
            name: t('Status'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: [
                {cid: SurveyInvitationStatus.NOT_OPENED, name: t('Not opened'), colorCode: ''},
                {cid: SurveyInvitationStatus.OPENED, name: t('Opened'), colorCode: ''},
                {cid: SurveyInvitationStatus.IN_PROGRESS, name: t('In progress'), colorCode: ''},
                {cid: SurveyInvitationStatus.COMPLETED, name: t('Completed'), colorCode: ''},
                {cid: SurveyInvitationStatus.VOIDED, name: t('Voided'), colorCode: ''}
            ],
            enumValues: ALL_INVITATION_STATUSES,
            dataType: DataType.ENUM
        }
    ], [surveys, t]);

    const columns = useMemo<ColumnDef<SurveyInvitation>[]>(() => {
        const versionLabel = (version: number | undefined) => (
            version !== undefined ? t('v{{n}}', {n: version}) : '—'
        );
        const channelLabel = (channel: SurveyInvitationChannel | undefined) => (
            channel === SurveyInvitationChannel.EMAIL
                ? t('Email')
                : channel === SurveyInvitationChannel.LINK
                    ? t('Link')
                    : ''
        );
        const guestCategoriesLabel = (categories: SurveyInvitationGuestCategory[] | undefined) => (
            (categories ?? []).map(category => category.name).join(', ')
        );

        return [
            {
                // As on the submissions lists: the first icon opens the link, the second is the one-way action -
                // Void while the link is live - or, once the guest has answered, a check.
                id: 'actions',
                header: t('Actions'),
                cell: ({row}) => {
                    const invitation = row.original;
                    const completed = invitation.status === SurveyInvitationStatus.COMPLETED;
                    const canVoid = !readOnly
                        && !completed
                        && invitation.status !== SurveyInvitationStatus.VOIDED;

                    return (
                        <Stack direction={'row'} alignItems={'center'} gap={0.5} data-no-row-click>
                            <Tooltip title={t('Share link')}>
                                <IconButton size={'small'} onClick={() => setSelectedInvitation(invitation)}>
                                    <ShareOutlinedIcon fontSize={'small'}/>
                                </IconButton>
                            </Tooltip>

                            {completed ? (
                                <Tooltip title={t('Invitation status COMPLETED')}>
                                    {/* The small icon button's own padding, so both marks sit in the same spot. */}
                                    <Box component={'span'} sx={{display: 'inline-flex', p: '5px'}}>
                                        <CheckIcon fontSize={'small'} color={'success'}/>
                                    </Box>
                                </Tooltip>
                            ) : canVoid && (
                                <Tooltip title={t('Void link')}>
                                    <IconButton size={'small'} onClick={() => setVoidTarget(invitation)}>
                                        <LinkOffIcon fontSize={'small'}/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    );
                },
                size: 104,
                enableResizing: false,
                meta: {defaultActive: true, defaultPinned: true}
            },
            {
                id: 'surveyName',
                header: t('Survey'),
                accessorFn: invitation => invitation.surveyName,
                cell: ({row}) => (
                    <Typography variant={'body1'} noWrap>
                        {row.original.surveyName ?? ''}
                    </Typography>
                ),
                size: 240,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => row.original.surveyName ?? ''
                }
            },
            {
                id: 'sentAt',
                header: t('Sent'),
                accessorFn: invitation => invitation.sentAt,
                cell: ({row}) => row.original.sentAt
                    ? <DateElement date={row.original.sentAt}/>
                    : '—',
                size: 190,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.sentAt}/>
                }
            },
            {
                id: 'guestName',
                header: t('Guest'),
                accessorFn: invitation => invitation.guestName,
                cell: ({row}) => (
                    <Typography variant={'body1'} noWrap>
                        {row.original.guestName || '—'}
                    </Typography>
                ),
                size: 260,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    grow: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => row.original.guestName ?? ''
                }
            },
            {
                id: 'guestLanguage',
                header: t('Language'),
                accessorFn: invitation => invitation.guestLanguage,
                cell: ({row}) => (
                    <LanguagesElement
                        languageCodes={row.original.guestLanguage ? [row.original.guestLanguage] : undefined}
                    />
                ),
                size: 130,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => (
                        <LanguagesElement
                            languageCodes={row.original.guestLanguage ? [row.original.guestLanguage] : undefined}
                            asPrint
                        />
                    )
                }
            },
            {
                id: 'surveyVersion',
                header: t('Version'),
                accessorFn: invitation => invitation.surveyVersion,
                cell: ({row}) => versionLabel(row.original.surveyVersion),
                size: 120,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => versionLabel(row.original.surveyVersion)
                }
            },
            {
                id: 'status',
                header: t('Status'),
                accessorFn: invitation => invitation.status,
                cell: ({row}) => (
                    <SurveyInvitationStatusChip
                        status={row.original.status}
                        furthestQuestionIndex={row.original.furthestQuestionIndex}
                    />
                ),
                size: 180,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => (
                        <SurveyInvitationStatusChip
                            status={row.original.status}
                            furthestQuestionIndex={row.original.furthestQuestionIndex}
                            asPrint
                        />
                    )
                }
            },
            {
                id: 'id',
                header: t('ID'),
                accessorFn: invitation => invitation.id,
                cell: ({row}) => row.original.id,
                size: 260,
                meta: {defaultActive: false, isServerSortable: true}
            },
            {
                id: 'reservationLabel',
                header: t('Reservation code'),
                accessorFn: invitation => invitation.reservationLabel,
                cell: ({row}) => row.original.reservationLabel ?? '',
                size: 180,
                meta: {defaultActive: true, isServerSortable: true, printable: true}
            },
            {
                id: 'reservationArrivalDay',
                header: t('Arrival'),
                accessorFn: invitation => invitation.reservationArrivalDay,
                cell: ({row}) => <DateElement date={row.original.reservationArrivalDay} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => <DateElement date={row.original.reservationArrivalDay} dateOnly/>
                }
            },
            {
                id: 'sentByName',
                header: t('Sent by'),
                accessorFn: invitation => invitation.sentByName,
                cell: ({row}) => row.original.sentByName ?? '',
                size: 180,
                meta: {defaultActive: true, isServerSortable: true, printable: true}
            },
            {
                id: 'openedAt',
                header: t('Opened at'),
                accessorFn: invitation => invitation.openedAt,
                cell: ({row}) => <DateElement date={row.original.openedAt}/>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => <DateElement date={row.original.openedAt}/>
                }
            },
            {
                id: 'lastActivityAt',
                header: t('Last activity'),
                accessorFn: invitation => invitation.lastActivityAt,
                cell: ({row}) => <DateElement date={row.original.lastActivityAt}/>,
                size: 190,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => <DateElement date={row.original.lastActivityAt}/>
                }
            },
            {
                id: 'reminderFiredAt',
                header: t('Reminder sent'),
                accessorFn: invitation => invitation.reminderFiredAt,
                cell: ({row}) => <DateElement date={row.original.reminderFiredAt}/>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => <DateElement date={row.original.reminderFiredAt}/>
                }
            },
            {
                id: 'answeredAt',
                header: t('Answered at'),
                accessorFn: invitation => invitation.answeredAt,
                cell: ({row}) => <DateElement date={row.original.answeredAt}/>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => <DateElement date={row.original.answeredAt}/>
                }
            },
            {
                id: 'voidedAt',
                header: t('Voided at'),
                accessorFn: invitation => invitation.voidedAt,
                cell: ({row}) => <DateElement date={row.original.voidedAt}/>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => <DateElement date={row.original.voidedAt}/>
                }
            },
            {
                id: 'channel',
                header: t('Channel'),
                accessorFn: invitation => invitation.channel,
                cell: ({row}) => channelLabel(row.original.channel),
                size: 140,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => channelLabel(row.original.channel)
                }
            },
            {
                id: 'guestCategories',
                header: t('Guest categories'),
                accessorFn: invitation => guestCategoriesLabel(invitation.guestCategories),
                cell: ({row}) => guestCategoriesLabel(row.original.guestCategories),
                size: 200,
                meta: {
                    defaultActive: false,
                    isServerSortable: false,
                    printable: true,
                    printCell: row => guestCategoriesLabel(row.original.guestCategories)
                }
            }
        ];
    }, [readOnly, t]);

    const controller = useServerTable<SurveyInvitation>({
        tableId: SURVEY_INVITATIONS_TABLE_ID,
        presentation: 'standard',
        columns,
        getRowId: invitation => invitation.id,
        onRowClick: invitation => setSelectedInvitation(invitation),
        defaultPageSize: 25,
        initialSorting: [{id: 'sentAt', desc: true}],
        urlState: INVITATIONS_URL_STATE,
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const page = await SurveyService.invitationList({
                surveyIds: filterTextValues(filterRules, 'surveyId'),
                query: searchQuery || undefined,
                languages: filterTextValues(filterRules, 'guestLanguage'),
                surveyVersions: filterNumberValues(filterRules, 'surveyVersion'),
                statuses: filterEnumValues(filterRules, 'status', ALL_INVITATION_STATUSES)
                    ?? ALL_INVITATION_STATUSES,
                sentAt: filterDateRange(filterRules, 'sentAt'),
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    // Versions are numbered per survey, so the Version filter appears once the list shows one survey.
    const surveyIds = filterTextValues(controller.filterRules, 'surveyId');
    const singleSurveyId = surveyIds?.length === 1 ? surveyIds[0] : undefined;
    const singleSurveyQuery = useQuery(
        ['survey', singleSurveyId],
        () => SurveyService.getSurveyById(singleSurveyId!),
        {enabled: !!singleSurveyId, refetchOnWindowFocus: false}
    );

    const singleSurveyVersion = singleSurveyId ? singleSurveyQuery.data?.version ?? 0 : 0;
    const tableFilterOptions = useMemo<FilterOption[]>(() => {
        if (singleSurveyVersion < 1) return filterOptions;

        const versions = Array.from({length: singleSurveyVersion}, (_, index) => String(index + 1)).reverse();

        return [...filterOptions, {
            field: 'surveyVersion',
            name: t('Version'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: versions.map(version => ({cid: version, name: t('v{{n}}', {n: version}), colorCode: ''})),
            enumValues: versions,
            dataType: DataType.ENUM
        }];
    }, [filterOptions, singleSurveyVersion, t]);

    const voidInvitation = async () => {
        if (!voidTarget?.id) return;

        setVoiding(true);
        try {
            await SurveyService.invitationVoid({id: voidTarget.id});
            setVoidTarget(null);
            await refreshInvitations();
            enqueueSnackbar(t('Link voided'), {
                variant: 'success',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } catch (error) {
            console.log(error);
            enqueueSnackbar(t('This link could not be voided.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } finally {
            setVoiding(false);
        }
    };

    return (
        <SettingsListPage title={t('Invitations')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch placeholder={t('Search invitations')}/>
                        <ServerTableSort/>
                        <ServerTableFilter options={tableFilterOptions}/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTablePrint title={t('Invitations')}/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            {!!selectedInvitation && (
                <SurveyInvitationDialog
                    open
                    invitation={selectedInvitation}
                    onClose={() => setSelectedInvitation(null)}
                />
            )}

            <SimpleDialog
                open={!!voidTarget}
                onClose={() => setVoidTarget(null)}
                title={t('Void this link?')}
                message={t('The guest can no longer open this survey with it. The invitation stays in the list.')}
                confirmText={voiding ? t('Voiding...') : t('Void link')}
                cancelText={t('Cancel')}
                onConfirm={voidInvitation}
                onCancel={() => setVoidTarget(null)}
            />
        </SettingsListPage>
    );
};

export default SurveyInvitationsListPage;
